import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

// Simple toast store
type Toast = {
  id: string;
  title: string;
  message?: string;
  onClick?: () => void;
};

type NotificationContextType = {
  notify: (t: Omit<Toast, 'id'>) => void;
  enableSound: boolean;
  setEnableSound: (v: boolean) => void;
  requestBrowserPermission: () => Promise<NotificationPermission>;
};

const NotificationContext = createContext<NotificationContextType | null>(null);

export function useNotify() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotify must be used within NotificationProvider');
  return ctx;
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [enableSound, setEnableSound] = useState<boolean>(() => {
    const v = localStorage.getItem('notif_sound');
    return v ? v === 'true' : true;
  });

  useEffect(() => {
    localStorage.setItem('notif_sound', String(enableSound));
  }, [enableSound]);

  // Simple sound
  const audioRef = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    audioRef.current = new Audio('/sounds/alert.mp3');
    audioRef.current.volume = 0.6;
  }, []);

  const playSound = useCallback(() => {
    if (enableSound && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [enableSound]);

  const notify = useCallback((t: Omit<Toast, 'id'>) => {
    const toast: Toast = { id: crypto.randomUUID(), ...t };
    setToasts((prev) => [toast, ...prev].slice(0, 5));
    playSound();
    // auto-hide
    setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== toast.id));
    }, 5000);
  }, [playSound]);

  const requestBrowserPermission = useCallback(async () => {
    if (!('Notification' in window)) return 'denied';
    if (Notification.permission === 'default') {
      return await Notification.requestPermission();
    }
    return Notification.permission;
  }, []);

  return (
    <NotificationContext.Provider value={{ notify, enableSound, setEnableSound, requestBrowserPermission }}>
      {children}
      {/* Toast UI */}
      <div className="fixed right-4 top-4 z-50 space-y-2 w-[320px] max-w-[90vw]">
        {toasts.map(t => (
          <button
            key={t.id}
            onClick={() => { t.onClick?.(); }}
            className="w-full text-left bg-white shadow-lg border rounded-lg p-3 hover:bg-gray-50"
          >
            <div className="font-semibold text-gray-900">{t.title}</div>
            {t.message && <div className="text-sm text-gray-600 mt-1 line-clamp-2">{t.message}</div>}
          </button>
        ))}
      </div>
    </NotificationContext.Provider>
  );
}
